import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Avatar,
  Divider,
} from "@mui/material";
import {
  GroupOutlined as CapacityIcon,
  Filter9PlusOutlined as PickIcon,
  ScheduleOutlined as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  HandshakeOutlined as PartnerIcon,
  Verified as VerifiedIcon,
  LocalFireDepartment as FireIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  getLotteryTypeConfig,
  STATUS_CONFIG,
  resultStatusTypeMapper,
  Path,
} from "../../../../../static/Constants";
import { safeFromWei } from "../../../../../static/utils";
import getImageUrlByHash from "../../../../../static/logoUtils";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const PublicLotteryCard = ({
  lottery,
  currency,
  partner,
  partnerLogoData,
}) => {
  const history = useHistory();

  const typeId = Number((lottery && lottery.lotteryType) || 0);
  const statusId = Number((lottery && lottery.status) || 0);

  const gameTypeConfig = getLotteryTypeConfig(typeId);

  const statusConfig = STATUS_CONFIG[statusId] || {
    label: resultStatusTypeMapper[statusId] || "Unknown",
    variant: "neutral",
  };

  const isOpen =
    typeId === 0
      ? statusId === 0 || statusId === 5
      : typeId === 1
      ? (statusId === 0 || statusId === 5) && Boolean(lottery && lottery.lotteryId)
      : typeId === 2
      ? statusId === 0 || statusId === 3 || statusId === 5
      : statusId !== 2 && (statusId === 0 || statusId === 3 || statusId === 5);

  const isRollover =
    statusId === 3 ||
    statusId === 5 ||
    Boolean(lottery && lottery.rolloverperct && Number(lottery.rolloverperct) > 0);

  const lotteryId = lottery && lottery.lotteryId;
  const playUrl = Path.getLotteryPlayUrl(lotteryId, typeId);
  const detailsUrl = Path.getLotteryDetails(lotteryId);

  const entryFeeInCurrency = safeFromWei(lottery && lottery.entryFee);
  const prizeInCurrency = safeFromWei(lottery && lottery.totalPrize);

  const partnerLogoUrl =
    partner && partner.logoHash
      ? getImageUrlByHash(partner.logoHash, partnerLogoData)
      : null;

  const TypeIcon = gameTypeConfig.icon;

  const deployedDateFormatted =
    lottery && lottery.deployedOn
      ? moment.unix(Number(lottery.deployedOn)).format("DD MMM YYYY")
      : null;

  return (
    <Card
      className="public-lottery-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          borderColor: gameTypeConfig.color,
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          height: 4,
          background: gameTypeConfig.gradient,
        }}
      />

      <Box sx={{ p: 2.75, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Badges Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {/* Game Type Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            {TypeIcon && (
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: gameTypeConfig.bgColor,
                  color: gameTypeConfig.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TypeIcon sx={{ fontSize: 18 }} />
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: gameTypeConfig.color,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.78rem",
              }}
            >
              {gameTypeConfig.name}
            </Typography>
          </Stack>

          {/* Status Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            {isRollover && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  color: colors.amber[700],
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <FireIcon sx={{ fontSize: 13, color: colors.amber[600] }} />
                <span>Rollover</span>
              </Box>
            )}
            {isOpen ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                  color: colors.emerald[700],
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <Box className="live-indicator-dot" />
                <span>Live</span>
              </Box>
            ) : (
              <CommonChip
                label={statusConfig.label}
                variant={statusConfig.variant}
                size="small"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
        </Stack>

        {/* Title & Date */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 2 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: colors.slate[900],
              fontSize: "1.25rem",
              cursor: "pointer",
              "&:hover": { color: colors.primary.main },
            }}
            onClick={() => history.push(detailsUrl)}
          >
            Lottery #{lotteryId}
          </Typography>

          {deployedDateFormatted && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ClockIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
              <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 500 }}>
                {deployedDateFormatted}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Prize Pool Callout Box */}
        <Box
          sx={{
            p: 2.25,
            mb: 2.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.blue[50],
            border: `1px solid ${colors.blue[200]}`,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[600],
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.72rem",
              }}
            >
              Grand Prize Pool
            </Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.2,
                borderRadius: borderRadiusPx.sm,
                bgcolor: "#ffffff",
                border: `1px solid ${colors.blue[200]}`,
                color: colors.primary.dark,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            >
              Entry: {entryFeeInCurrency} {currency}
            </Typography>
          </Stack>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              fontSize: "1.75rem",
              background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {prizeInCurrency} {currency}
          </Typography>
        </Box>

        {/* Specifications Matrix */}
        <Stack spacing={1.25} sx={{ mb: 2.5, flexGrow: 1 }}>
          {/* Capacity / Slots */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <CapacityIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
              <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.85rem" }}>
                Total Ticket Capacity
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[800], fontSize: "0.875rem" }}>
              {(lottery && lottery.capacity) || "Unlimited"} Slots
            </Typography>
          </Stack>

          {/* Pick Numbers */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <PickIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
              <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.85rem" }}>
                Required Numbers
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[800], fontSize: "0.875rem" }}>
              {(lottery && lottery.pickNumbers) || "1"} {Number(lottery && lottery.pickNumbers) === 1 ? "Number" : "Numbers"}
            </Typography>
          </Stack>

          {/* Minimum Players (for MegaBET) */}
          {typeId === 2 && lottery && lottery.minPlayers && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <CapacityIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />
                <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.85rem" }}>
                  Minimum Players
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.emerald[700], fontSize: "0.875rem" }}>
                {lottery.minPlayers} Players
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Partner / Co-host Badge (if present) */}
        {partner && partner.partnerAddress && partner.partnerAddress !== "0x0000000000000000000000000000000000000000" && (
          <Box
            sx={{
              p: 1.25,
              mb: 2.5,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.purple[50],
              border: `1px solid ${colors.purple[200]}`,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Avatar
              src={partnerLogoUrl}
              alt={(partner && partner.name) || "Partner"}
              sx={{
                width: 28,
                height: 28,
                borderRadius: borderRadiusPx.sm,
                bgcolor: colors.purple[100],
              }}
            >
              <PartnerIcon sx={{ fontSize: 16, color: colors.purple[700] }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "0.78rem" }}
                >
                  {(partner && partner.name) || "Co-Host Partner"}
                </Typography>
                <VerifiedIcon sx={{ fontSize: 13, color: colors.purple[600] }} />
              </Stack>
              <Typography variant="caption" sx={{ color: colors.slate[500], fontSize: "0.7rem", display: "block" }}>
                Platform Beneficiary
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2, borderColor: colors.slate[100] }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CommonButton
            variant="outline"
            size="small"
            onClick={() => history.push(detailsUrl)}
            sx={{
              flex: 1,
              fontSize: "0.85rem",
              py: 0.9,
            }}
          >
            Details
          </CommonButton>

          {isOpen ? (
            <CommonButton
              variant="playNow"
              size="small"
              onClick={() => history.push(playUrl)}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{
                flex: 1.35,
                fontSize: "0.85rem",
                py: 0.9,
                background: gameTypeConfig.gradient,
                boxShadow: gameTypeConfig.boxShadow,
                "&:hover": {
                  background: gameTypeConfig.gradient,
                  boxShadow: `0 6px 20px ${gameTypeConfig.glow || "rgba(0,0,0,0.2)"}`,
                  transform: "translateY(-1px)",
                },
              }}
            >
              Play Now
            </CommonButton>
          ) : (
            <CommonButton
              variant="outline"
              size="small"
              onClick={() => history.push(detailsUrl)}
              sx={{
                flex: 1.35,
                fontSize: "0.85rem",
                py: 0.9,
                color: colors.slate[500],
              }}
            >
              View Draw
            </CommonButton>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

PublicLotteryCard.propTypes = {
  lottery: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  partner: PropTypes.object,
  partnerLogoData: PropTypes.array,
};

export default PublicLotteryCard;
